/**
 * OL-2 cost rollup derivation for Ops Lookup.
 * Theoretical = sale-side FullCost; actual = build_commit FullCost when present.
 * Dealer cost is NEVER auto-filled from catalog wholesale.
 */

export type OpsLookupCostSnapLike = {
  full_cost_usd?: number | null;
  materials_cost_usd?: number | null;
  estimated_materials_cost?: number | null;
  channel_prices_usd?: {
    retail?: number | null;
    wholesale?: number | null;
    rto?: number | null;
    employee?: number | null;
  } | null;
  metadata?: Record<string, unknown> | null;
};

export type OpsLookupCostRollup = {
  theoreticalUsd: number | null;
  actualUsd: number | null;
  varianceUsd: number | null;
  retailUsd: number | null;
  /** Explicit dealer cost only — never aliased from wholesale. */
  dealerCostUsd: number | null;
  /** Catalog wholesale for reference; not dealer cost. */
  catalogWholesaleUsd: number | null;
  theoreticalSource: "sale_commit" | "preview" | "materials" | null;
  actualSource: "build_commit" | null;
  dealerCostSource: "deal_metadata" | "snapshot_metadata" | null;
};

function finiteOrNull(n: unknown): number | null {
  const v = typeof n === "number" ? n : Number(n);
  return Number.isFinite(v) ? v : null;
}

function readMetaNumber(meta: Record<string, unknown> | null | undefined, key: string): number | null {
  if (!meta) return null;
  return finiteOrNull(meta[key]);
}

/**
 * Prefer sale snapshot FullCost; else live preview; else materials-only estimate.
 * Actual prefers build_commit FullCost (post-build freeze).
 */
export function deriveOpsLookupCostRollup(input: {
  saleSnapshot?: OpsLookupCostSnapLike | null;
  buildSnapshot?: OpsLookupCostSnapLike | null;
  preview?: OpsLookupCostSnapLike | null;
  dealMetadata?: Record<string, unknown> | null;
}): OpsLookupCostRollup {
  const sale = input.saleSnapshot ?? null;
  const build = input.buildSnapshot ?? null;
  const preview = input.preview ?? null;
  const dealMeta = input.dealMetadata ?? null;

  let theoreticalUsd: number | null = null;
  let theoreticalSource: OpsLookupCostRollup["theoreticalSource"] = null;
  const saleFull = finiteOrNull(sale?.full_cost_usd);
  const previewFull = finiteOrNull(preview?.full_cost_usd);
  const materials =
    finiteOrNull(sale?.materials_cost_usd) ??
    finiteOrNull(sale?.estimated_materials_cost) ??
    finiteOrNull(preview?.materials_cost_usd) ??
    finiteOrNull(preview?.estimated_materials_cost);

  if (saleFull != null) {
    theoreticalUsd = saleFull;
    theoreticalSource = "sale_commit";
  } else if (previewFull != null) {
    theoreticalUsd = previewFull;
    theoreticalSource = "preview";
  } else if (materials != null) {
    theoreticalUsd = materials;
    theoreticalSource = "materials";
  }

  const actualUsd = finiteOrNull(build?.full_cost_usd);
  const actualSource: OpsLookupCostRollup["actualSource"] =
    actualUsd != null ? "build_commit" : null;

  const varianceUsd =
    theoreticalUsd != null && actualUsd != null ? actualUsd - theoreticalUsd : null;

  const retailUsd =
    finiteOrNull(sale?.channel_prices_usd?.retail) ??
    finiteOrNull(preview?.channel_prices_usd?.retail) ??
    finiteOrNull(build?.channel_prices_usd?.retail);

  const catalogWholesaleUsd =
    finiteOrNull(sale?.channel_prices_usd?.wholesale) ??
    finiteOrNull(preview?.channel_prices_usd?.wholesale) ??
    finiteOrNull(build?.channel_prices_usd?.wholesale);

  const fromDeal = readMetaNumber(dealMeta, "dealer_cost_usd");
  const fromSaleMeta = readMetaNumber(sale?.metadata ?? null, "dealer_cost_usd");
  const fromBuildMeta = readMetaNumber(build?.metadata ?? null, "dealer_cost_usd");

  let dealerCostUsd: number | null = null;
  let dealerCostSource: OpsLookupCostRollup["dealerCostSource"] = null;
  if (fromDeal != null) {
    dealerCostUsd = fromDeal;
    dealerCostSource = "deal_metadata";
  } else if (fromSaleMeta != null) {
    dealerCostUsd = fromSaleMeta;
    dealerCostSource = "snapshot_metadata";
  } else if (fromBuildMeta != null) {
    dealerCostUsd = fromBuildMeta;
    dealerCostSource = "snapshot_metadata";
  }

  return {
    theoreticalUsd,
    actualUsd,
    varianceUsd,
    retailUsd,
    dealerCostUsd,
    catalogWholesaleUsd,
    theoreticalSource,
    actualSource,
    dealerCostSource,
  };
}

export function formatOpsLookupUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return (
    "$" +
    n.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  );
}
